import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';

import { Panel, PanelType, DefaultButton, PrimaryButton, IconButton, Overlay } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';
import { findParentElementWithClass, ClarityTags, useClarityTags } from '@bingads-webui-campaign-react/asset-library-model';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';

import { panelStylesProp, layerProps } from '../shared-containers/styles/panel-container-style';
import { getPanelStyles } from './styles';

const AddMediaPanelInternal = (props) => {
  const {
    isOpen,
    setIsOpen,
    initialView,
    managedViewSelectionState,
    updateManagedViewSelectionState,
    cid,
    i18n,
    aid,
    errorMessage,
    addMediaConfig,
    logger,
    eventHandler,
  } = props;

  const classes = useDefaultStyles(getPanelStyles);
  const [currentView, setCurrentView] = useState(initialView);
  const [hideSubtitle, setHideSubtitle] = useState(false);
  const [showBlocker, setShowBlocker] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showApplyButton, setShowApplyButton] = useState(false);
  const [disableApplyButton, setDisableApplyButton] = useState(false);
  const [selectionInfoText, setSelectionInfoText] = useState('');
  const triggerAddImagesRef = useRef();

  useClarityTags([ClarityTags.LaunchedAddAssetsFromCrudAd]);

  const currentSelectedView = useMemo(() => managedViewSelectionState || currentView, [
    currentView,
    managedViewSelectionState,
  ]);

  useEffect(() => {
    setErrors([errorMessage]);
  }, [errorMessage]);

  useEffect(() => {
    if (!isOpen) {
      setErrors([]);
      setShowApplyButton(false);
      setSelectionInfoText('');
      setDisableApplyButton(false);
      setHideSubtitle(false);
    }
  }, [isOpen]);

  const updateSelectedView = useCallback((newView) => {
    if (managedViewSelectionState) {
      updateManagedViewSelectionState(newView);
    } else {
      setCurrentView(newView);
    }
  }, [managedViewSelectionState, updateManagedViewSelectionState]);

  const goToView = useCallback((view) => {
    if (logger) {
      logger.logTrace('image tab clicked');
    }

    updateSelectedView(view);
  }, [updateSelectedView, logger]);

  const resetFocus = useCallback((prevView) => {
    if (prevView === 'AI-generated') {
      setTimeout(() => {
        const generateImagesButton = document.querySelector('#aigc-generate-images-button');
        generateImagesButton.focus();
      }, 0);
    }
  }, []);

  const goBackToDefault = useCallback(() => {
    updateSelectedView(initialView);
    resetFocus(currentSelectedView);
  }, [currentSelectedView, updateSelectedView, initialView, resetFocus]);

  const {
    title,
    onRenderSubtitle,
    onRenderContent,
    onRenderFooterContent,
    onRenderHeaderContent,
    showBackButton,
  } = addMediaConfig[currentSelectedView];

  const overlayStyles = useMemo(() => ({
    root: { zIndex: 2 },
  }), []);

  // don't allow to close panel by losing focus
  const onDismiss = useCallback((evt) => {
    const target = evt && evt.currentTarget;
    const addMediaPanelElem = findParentElementWithClass(target, classes.panel);
    if (addMediaPanelElem || evt.key === 'Escape') {
      setIsOpen(false);
    }
  }, [classes.panel, setIsOpen]);

  return (
    <Panel
      headerText={i18n.getString(title)}
      className={classnames(classes.panel, 'add-media-panel-crud-ad')}
      isOpen={isOpen}
      type={PanelType.custom}
      customWidth="940px"
      onDismiss={onDismiss}
      isFooterAtBottom
      closeButtonAriaLabel={i18n.getString(_TL_('close'))}
      onRenderHeader={() => (
        <div className={classes.panelHeader}>
          {showBackButton &&
            <div className={classes.backButton}>
              <IconButton iconProps={{ iconName: 'Back' }} className="back-button" onClick={() => goBackToDefault()} ariaLabel={i18n.getString(_TL_('back'))} />
            </div>
          }
          <div className={classnames(classes.panelTitle, 'panel-title')}>
            {i18n.getString(title)}
          </div>
          {onRenderHeaderContent && onRenderHeaderContent({ ...props })}
        </div>
      )}
      onRenderFooterContent={() => (
        <React.Fragment>
          {onRenderFooterContent && onRenderFooterContent({ ...props })}
          <span className={classnames('error-message', classes.errorMessage)} role="alert" aria-live={errors.length > 0 ? 'assertive' : 'off'}>
            {
              errors.length > 0 && errors.map(error => error)
            }
          </span>
          {
            showApplyButton && (
              <PrimaryButton onClick={() => triggerAddImagesRef.current()} className={classnames('apply-button', 'add-image-apply-button', classes.applyButton)} text={i18n.getString(_TL_('Apply'))} disabled={disableApplyButton} />
            )
          }
          <DefaultButton onClick={() => setIsOpen(false)} className={classnames('cancel-button', 'add-image-cancel-button')} text={i18n.getString(_TL_('Cancel'))} />
          {
            selectionInfoText && (
              <div className={classnames('selection-info-text', classes.selectionInfoText)}>
                {selectionInfoText}
              </div>
            )
          }
        </React.Fragment>
      )}
      styles={panelStylesProp}
      layerProps={layerProps}
    >
      {
        onRenderSubtitle && !hideSubtitle &&
          onRenderSubtitle({
            subtitleClass: classes.subtitle,
            i18n,
            aid,
            cid,
            onAssetLibraryLinkClick: (isAdStudioEnabled) => {
              if (isAdStudioEnabled) {
                logger.logTrace('Ads studio link in Ad creation flow panel clicked');
              } else {
                logger.logTrace('Asset library link in Ad creation flow panel clicked');
              }
            },
          })
      }
      {
        showBlocker &&
        <Overlay isDarkThemed={false} styles={overlayStyles}>
          <AssetLibrarySpinner useCustomSize />
        </Overlay>
      }
      {
        onRenderContent({
          setHideSubtitle,
          setShowBlocker,
          setErrors,
          goToView,
          setShowApplyButton,
          setDisableApplyButton,
          setSelectionInfoText,
          triggerAddImagesRef,
          eventHandler,
          ...props,
        })
      }
    </Panel>
  );
};

AddMediaPanelInternal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  initialView: PropTypes.string,
  managedViewSelectionState: PropTypes.string,
  updateManagedViewSelectionState: PropTypes.func,
  assetLibraryConfig: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  aid: PropTypes.string.isRequired,
  cid: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  addMediaConfig: PropTypes.shape({}),
  eventHandler: PropTypes.shape({}),
};

AddMediaPanelInternal.defaultProps = {
  initialView: _.noop,
  managedViewSelectionState: null,
  updateManagedViewSelectionState: _.noop,
  errorMessage: '',
  addMediaConfig: _.noop,
  eventHandler: {},
};

export const AddMediaPanel = withPerfMarker(AddMediaPanelInternal);
